import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api, getErrorMessage } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import type { AdminUser } from '../types';
import Swal from 'sweetalert2';
import {
  Users,
  UserPlus,
  Shield,
  ShieldCheck,
  ShieldAlert,
  KeyRound,
  Unlock,
  Trash2,
  Edit3,
  Search,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Lock,
  Sparkles,
  Copy,
  Check,
  Eye,
  EyeOff,
} from 'lucide-react';

const ROLE_CONFIG: Record<
  AdminUser['role'],
  { label: string; badgeClass: string; description: string; permissions: string[] }
> = {
  super_admin: {
    label: 'Super Admin',
    badgeClass:
      'bg-purple-100 dark:bg-purple-950/60 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-800',
    description:
      'Acesso total irrestrito à plataforma, segurança, Kong Gateway e gestão de equipa.',
    permissions: [
      'Gestão de Equipa',
      'Kong & Rotas',
      'Produtos & Planos',
      'Clientes & Faturação',
      'Auditoria',
    ],
  },
  admin: {
    label: 'Administrador',
    badgeClass:
      'bg-blue-100 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800',
    description: 'Gestão operacional completa de produtos, clientes, subscrições e configurações.',
    permissions: [
      'Produtos & Planos',
      'Clientes & Subscrições',
      'Rotas & Mocks',
      'Logs & Auditoria',
    ],
  },
  developer: {
    label: 'Engenharia / DevOps',
    badgeClass:
      'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800',
    description: 'Configuração técnica de APIs, OpenAPI/Swagger, transformações, Kong e Sandbox.',
    permissions: ['Catálogo de APIs', 'Endpoints & Kong', 'Transformações', 'Logs de API'],
  },
  finance: {
    label: 'Financeiro',
    badgeClass:
      'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800',
    description:
      'Gestão de planos tarifários, métodos de pagamento, faturas e receitas de clientes.',
    permissions: [
      'Planos & Preços',
      'Métodos de Pagamento',
      'Subscrições',
      'Relatórios de Receita',
    ],
  },
  support: {
    label: 'Suporte Técnico',
    badgeClass:
      'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700',
    description:
      'Atendimento a tickets de suporte, consulta de clientes, subscrições e diagnóstico de logs.',
    permissions: ['Tickets de Suporte', 'Consulta de Clientes', 'Diagnóstico de Logs', 'Alertas'],
  },
};

export const AdminUsers: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'locked'>('all');

  // Create / Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'admin' as AdminUser['role'],
    password: '',
    is_active: true,
  });

  // Password Reset Modal
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/users');
      setUsers(response.data.data || []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const generateStrongPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
    let pwd = 'Ts@';
    for (let i = 0; i < 11; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pwd;
  };

  const handleOpenCreate = () => {
    setEditingUser(null);
    const generated = generateStrongPassword();
    setFormData({
      name: '',
      email: '',
      role: 'admin',
      password: generated,
      is_active: true,
    });
    setShowPassword(true);
    setShowModal(true);
  };

  const handleOpenEdit = (admin: AdminUser) => {
    setEditingUser(admin);
    setFormData({
      name: admin.name || '',
      email: admin.email,
      role: admin.role,
      password: '',
      is_active: admin.is_active,
    });
    setShowModal(true);
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (editingUser) {
        await api.put(`/admin/users/${editingUser.id}`, {
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role,
          is_active: formData.is_active,
        });
        showToast(`Administrador "${formData.name}" atualizado com sucesso!`);
      } else {
        await api.post('/admin/users', {
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role,
          password: formData.password,
        });
        showToast(`Administrador "${formData.name}" criado com sucesso!`);
      }
      setShowModal(false);
      await fetchAdminUsers();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (admin: AdminUser) => {
    if (admin.id === currentUser?.id && admin.is_active) {
      Swal.fire({
        icon: 'warning',
        title: 'Ação não permitida',
        text: 'Não pode desativar a sua própria conta de administrador.',
      });
      return;
    }

    try {
      await api.patch(`/admin/users/${admin.id}/status`, {
        is_active: !admin.is_active,
      });
      showToast(
        `Conta de ${admin.name || admin.email} ${!admin.is_active ? 'ativada' : 'desativada'}.`
      );
      await fetchAdminUsers();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleUnlockUser = async (admin: AdminUser) => {
    try {
      await api.post(`/admin/users/${admin.id}/unlock`);
      showToast(`Conta de ${admin.name || admin.email} desbloqueada com sucesso!`);
      await fetchAdminUsers();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleOpenResetPassword = (admin: AdminUser) => {
    setResetTarget(admin);
    setNewPassword(generateStrongPassword());
    setShowPassword(true);
  };

  const handleConfirmResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetTarget) return;
    setResetSubmitting(true);
    setError('');
    try {
      await api.put(`/admin/users/${resetTarget.id}/password`, {
        password: newPassword,
      });
      showToast(`Senha de ${resetTarget.name || resetTarget.email} redefinida com sucesso!`);
      setResetTarget(null);
      await fetchAdminUsers();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setResetSubmitting(false);
    }
  };

  const handleDeleteUser = async (admin: AdminUser) => {
    if (admin.id === currentUser?.id) {
      Swal.fire({
        icon: 'warning',
        title: 'Ação não permitida',
        text: 'Não pode eliminar a sua própria conta ativa.',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Eliminar Administrador?',
      text: `Tem a certeza que deseja remover "${admin.name || admin.email}" (${ROLE_CONFIG[admin.role]?.label || admin.role}) do Portal de Administração?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e11d48',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sim, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/admin/users/${admin.id}`);
      showToast(`Administrador "${admin.name || admin.email}" removido.`);
      await fetchAdminUsers();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const copyPassword = (pwd: string) => {
    navigator.clipboard.writeText(pwd);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const isUserLocked = (u: AdminUser) => {
    if (!u.locked_until) return false;
    return new Date(u.locked_until) > new Date();
  };

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const matchesSearch =
        !searchTerm ||
        (u.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'all' || u.role === roleFilter;
      const locked = isUserLocked(u);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'locked' && locked) ||
        (statusFilter === 'active' && u.is_active && !locked) ||
        (statusFilter === 'inactive' && !u.is_active);
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.is_active && !isUserLocked(u)).length;
    const superAdmins = users.filter((u) => u.role === 'super_admin').length;
    const mfaEnabled = users.filter((u) => u.mfa_enabled).length;
    const locked = users.filter((u) => isUserLocked(u)).length;
    return { total, active, superAdmins, mfaEnabled, locked };
  }, [users]);

  const getInitials = (name?: string, email?: string) => {
    const source = name || email || 'AD';
    const parts = source.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return source.slice(0, 2).toUpperCase();
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-950/60 border border-primary-200 dark:border-primary-800 flex items-center justify-center text-primary-600 dark:text-primary-400">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                Equipa & Administradores
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Gerencie os utilizadores internos do Portal Admin, perfis de acesso (RBAC), MFA e
                segurança de contas.
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/mfa-setup"
            className="btn-base btn-md btn-secondary gap-2"
            title="Configurar Autenticação de Dois Fatores (2FA) para a sua conta"
          >
            <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span>Meu 2FA / MFA</span>
          </Link>
          <button
            onClick={fetchAdminUsers}
            className="btn-base btn-md btn-secondary gap-2"
            title="Atualizar lista"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
          <button onClick={handleOpenCreate} className="btn-base btn-md btn-primary gap-2">
            <UserPlus className="w-4 h-4" />
            <span>Novo Administrador</span>
          </button>
        </div>
      </div>

      {/* Feedback Alerts */}
      {error && (
        <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 p-4 flex items-center justify-between text-rose-800 dark:text-rose-300 text-sm">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-xs font-semibold underline ml-4">
            Fechar
          </button>
        </div>
      )}

      {successMessage && (
        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 p-4 flex items-center gap-2 text-emerald-800 dark:text-emerald-300 text-sm font-medium">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Total na Equipa
            </p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.total}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Contas registadas no portal
            </p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
            <Users className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Contas Ativas
            </p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
              {stats.active}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Com acesso imediato ao painel
            </p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Super Admins
            </p>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
              {stats.superAdmins}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Privilégio máximo de governação
            </p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 flex items-center justify-center">
            <Shield className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              2FA / Bloqueios
            </p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-bold text-slate-900 dark:text-white">
                {stats.mfaEnabled} MFA
              </span>
              {stats.locked > 0 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                  {stats.locked} bloqueado(s)
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Proteção TOTP e anti-brute-force
            </p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* RBAC Matrix Overview */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Perfis de Acesso RBAC Disponíveis
          </h2>
          <span className="text-xs text-slate-400 dark:text-slate-500">
            Clique num perfil para filtrar a lista
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {(Object.keys(ROLE_CONFIG) as Array<AdminUser['role']>).map((roleKey) => {
            const cfg = ROLE_CONFIG[roleKey];
            const isSelected = roleFilter === roleKey;
            return (
              <button
                key={roleKey}
                type="button"
                onClick={() => setRoleFilter(isSelected ? 'all' : roleKey)}
                className={`text-left p-3.5 rounded-xl border transition-all ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-950/30 ring-1 ring-primary-500'
                    : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30'
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.badgeClass}`}
                  >
                    {cfg.label}
                  </span>
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                    {users.filter((u) => u.role === roleKey).length}
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                  {cfg.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters & Search Bar */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pesquisar por nome ou email do administrador..."
            className="input pl-10"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="select w-auto min-w-[170px]"
          >
            <option value="all">Todos os Perfis (Roles)</option>
            <option value="super_admin">Super Admin</option>
            <option value="admin">Administrador</option>
            <option value="developer">Engenharia / DevOps</option>
            <option value="finance">Financeiro</option>
            <option value="support">Suporte Técnico</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="select w-auto min-w-[150px]"
          >
            <option value="all">Todos os Estados</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
            <option value="locked">Bloqueados</option>
          </select>
        </div>
      </div>

      {/* Admin Users Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500 dark:text-slate-400 text-sm">
            A carregar equipa de administradores...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <Users className="w-10 h-10 text-slate-400 mx-auto" />
            <div className="text-base font-semibold text-slate-800 dark:text-slate-200">
              Nenhum administrador encontrado
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
              Não existem utilizadores correspondentes aos filtros aplicados.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Administrador</th>
                  <th>Perfil (RBAC)</th>
                  <th>Estado</th>
                  <th>Segurança 2FA</th>
                  <th>Última Atividade</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((admin) => {
                  const roleInfo = ROLE_CONFIG[admin.role] || ROLE_CONFIG.admin;
                  const locked = isUserLocked(admin);
                  const isSelf = admin.id === currentUser?.id;

                  return (
                    <tr key={admin.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-600 to-indigo-600 text-white font-bold text-xs flex items-center justify-center shrink-0 shadow-sm">
                            {getInitials(admin.name, admin.email)}
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                              <span>{admin.name || admin.email.split('@')[0]}</span>
                              {isSelf && (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-primary-100 dark:bg-primary-950 text-primary-700 dark:text-primary-300">
                                  Você
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {admin.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${roleInfo.badgeClass}`}
                        >
                          {roleInfo.label}
                        </span>
                      </td>

                      <td>
                        {locked ? (
                          <span className="badge badge-danger gap-1">
                            <Lock className="w-3 h-3" />
                            Bloqueado ({admin.failed_login_count} falhas)
                          </span>
                        ) : admin.is_active ? (
                          <span className="badge badge-success gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Ativo
                          </span>
                        ) : (
                          <span className="badge badge-gray gap-1">
                            <XCircle className="w-3 h-3" />
                            Inativo
                          </span>
                        )}
                      </td>

                      <td>
                        {admin.mfa_enabled ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                            <ShieldCheck className="w-4 h-4" />
                            TOTP Ativo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                            Não configurado
                          </span>
                        )}
                      </td>

                      <td className="text-xs text-slate-500 dark:text-slate-400">
                        {admin.last_active_at
                          ? new Date(admin.last_active_at).toLocaleString('pt-PT', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'Sem registo recente'}
                      </td>

                      <td className="text-right">
                        <div className="inline-flex items-center justify-end gap-1.5">
                          {locked && (
                            <button
                              onClick={() => handleUnlockUser(admin)}
                              className="btn-base btn-sm bg-amber-100 hover:bg-amber-200 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 gap-1"
                              title="Desbloquear conta"
                            >
                              <Unlock className="w-3.5 h-3.5" />
                              <span>Desbloquear</span>
                            </button>
                          )}

                          <button
                            onClick={() => handleOpenEdit(admin)}
                            className="p-2 rounded-lg text-slate-600 hover:text-primary-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-primary-400 dark:hover:bg-slate-800 transition-colors"
                            title="Editar perfil e permissões"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleOpenResetPassword(admin)}
                            className="p-2 rounded-lg text-slate-600 hover:text-amber-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-amber-400 dark:hover:bg-slate-800 transition-colors"
                            title="Redefinir senha"
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>

                          {!isSelf && (
                            <>
                              <button
                                onClick={() => handleToggleStatus(admin)}
                                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                                  admin.is_active
                                    ? 'text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/40'
                                    : 'text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40'
                                }`}
                                title={admin.is_active ? 'Desativar conta' : 'Ativar conta'}
                              >
                                {admin.is_active ? 'Desativar' : 'Ativar'}
                              </button>

                              <button
                                onClick={() => handleDeleteUser(admin)}
                                className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                                title="Eliminar administrador"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Admin User Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                {editingUser ? 'Editar Administrador' : 'Novo Administrador da Plataforma'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveUser} className="p-6 space-y-4">
              <div>
                <label className="label">Nome Completo *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Ana Martins"
                  className="input"
                />
              </div>

              <div>
                <label className="label">Email Corporativo *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="nome@tatusolutions.com"
                  className="input"
                />
              </div>

              <div>
                <label className="label">Perfil de Acesso (RBAC) *</label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value as AdminUser['role'] })
                  }
                  className="select"
                >
                  <option value="super_admin">Super Admin — Acesso Total</option>
                  <option value="admin">Administrador — Gestão Operacional</option>
                  <option value="developer">Engenharia / DevOps — APIs, Kong & Mocks</option>
                  <option value="finance">Financeiro — Planos, Pagamentos & Receitas</option>
                  <option value="support">Suporte Técnico — Tickets & Diagnóstico</option>
                </select>
                <div className="mt-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60">
                  <p className="text-xs text-slate-600 dark:text-slate-300 font-medium">
                    {ROLE_CONFIG[formData.role]?.description}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {ROLE_CONFIG[formData.role]?.permissions.map((perm) => (
                      <span
                        key={perm}
                        className="text-[11px] px-2 py-0.5 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                      >
                        {perm}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {!editingUser ? (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Senha Inicial *
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData({ ...formData, password: generateStrongPassword() })
                      }
                      className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      Gerar senha segura
                    </button>
                  </div>
                  <div className="relative flex items-center gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={8}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="input pr-10 font-mono text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyPassword(formData.password)}
                      className="btn-base btn-md btn-secondary gap-1.5 shrink-0"
                    >
                      {copiedPassword ? (
                        <>
                          <Check className="w-4 h-4 text-emerald-600" />
                          <span>Copiada</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copiar</span>
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Mínimo de 8 caracteres. Partilhe esta credencial de forma segura com o membro da
                    equipa.
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                  <div>
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                      Conta Ativa
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Desative para suspender temporariamente o acesso deste administrador.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    disabled={editingUser.id === currentUser?.id}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-5 w-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-base btn-md btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" disabled={submitting} className="btn-base btn-md btn-primary">
                  {submitting
                    ? 'A guardar...'
                    : editingUser
                      ? 'Guardar Alterações'
                      : 'Criar Administrador'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Redefinir Senha de Administrador
              </h3>
              <button
                onClick={() => setResetTarget(null)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleConfirmResetPassword} className="p-6 space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                A definir uma nova senha de acesso para{' '}
                <span className="font-semibold text-slate-900 dark:text-white">
                  {resetTarget.name || resetTarget.email}
                </span>
                . Caso a conta esteja bloqueada por tentativas falhadas, será desbloqueada
                automaticamente.
              </p>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Nova Senha *
                  </label>
                  <button
                    type="button"
                    onClick={() => setNewPassword(generateStrongPassword())}
                    className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Gerar outra
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => copyPassword(newPassword)}
                    className="btn-base btn-md btn-secondary shrink-0 gap-1"
                  >
                    {copiedPassword ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setResetTarget(null)}
                  className="btn-base btn-md btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={resetSubmitting}
                  className="btn-base btn-md btn-primary"
                >
                  {resetSubmitting ? 'A redefinir...' : 'Confirmar Nova Senha'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
